import { type FC, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';

import dibsCss from 'dibs-css';
import { FormattedMessage, useIntl } from 'dibs-react-intl';

import { getIsNewSeller } from '../../../utils/sellerHelpers.ts';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';
import { PdpSharedSellerAssociations } from './PdpSharedSellerAssociations.tsx';

import { type PdpSharedSellerInfo_viewer$key } from './__generated__/PdpSharedSellerInfo_viewer.graphql.ts';
import { type PdpSharedSellerInfo_seller$key } from './__generated__/PdpSharedSellerInfo_seller.graphql.ts';

const MINIMUM_ORDER_QUANTITY = 5;

const formatResponseTime = (
    responseTime: number | null | undefined,
    displayZeroResponseTime: boolean
): ReactNode => {
    if (responseTime === null || responseTime === undefined || isNaN(responseTime)) {
        return null;
    }

    if (responseTime === 0 && !displayZeroResponseTime) {
        return null;
    }

    if (responseTime > 168) {
        return (
            <FormattedMessage id="PdpSharedSellerInfo.responseTimeWeek" defaultMessage="A week" />
        );
    }

    if (responseTime > 48) {
        return (
            <FormattedMessage
                id="PdpSharedSellerInfo.responseTimeSeveralDays"
                defaultMessage="Several days"
            />
        );
    }

    if (responseTime > 23) {
        return (
            <FormattedMessage
                id="PdpSharedSellerInfo.responseTimeFewDays"
                defaultMessage="1 to 2 days"
            />
        );
    }

    return (
        <FormattedMessage
            id="PdpSharedSellerInfo.responseTimeValue"
            defaultMessage="{responseTime, select,
              0 {<1 hour}
              1 {1 hour}
              other {{responseTime} hours}
            }"
            values={{ responseTime }}
        />
    );
};

const viewerFragment = graphql`
    fragment PdpSharedSellerInfo_viewer on Viewer {
        suppressNewSellerSignals: featureFlag(feature: "suppressNewSellerSignals")
    }
`;

const sellerFragment = graphql`
    fragment PdpSharedSellerInfo_seller on Seller {
        ...PdpSharedSellerAssociations_seller
        agreementStart
        sellerOrderQuantity
        analytics {
            metrics {
                responseTime {
                    value
                }
            }
        }
        liveStorefrontProfile {
            establishmentYear
        }
        shippingAddress {
            displayFullCityStateCountry: displayCityStateCountry(fullCountryName: true)
        }
        monthsSinceStartDate: timeSinceStartDate(unit: MONTH)
    }
`;

export const PdpSharedSellerInfo: FC<{
    viewer: PdpSharedSellerInfo_viewer$key | null;
    seller: PdpSharedSellerInfo_seller$key;
    showLocation?: boolean;
    showAssociations?: boolean;
    displayZeroResponseTime?: boolean;
}> = ({
    viewer: viewerRef,
    seller: sellerRef,
    showLocation = false,
    showAssociations = true,
    displayZeroResponseTime = false,
}) => {
    const { formatNumber } = useIntl();
    const { isMobile } = useServerVarsContext();
    const viewer = useFragment(viewerFragment, viewerRef);
    const seller = useFragment(sellerFragment, sellerRef);
    const {
        analytics,
        agreementStart,
        sellerOrderQuantity,
        liveStorefrontProfile,
        monthsSinceStartDate,
    } = seller;
    const establishmentYear = liveStorefrontProfile?.establishmentYear;
    const dealerSince = agreementStart ? new Date(agreementStart).getUTCFullYear() : null;
    const isNewSeller = getIsNewSeller(monthsSinceStartDate);
    const showDealerSince = dealerSince && (!isNewSeller || !viewer?.suppressNewSellerSignals);
    const responseTime = formatResponseTime(
        analytics?.metrics?.responseTime?.value,
        displayZeroResponseTime
    );
    const sellerLocation = seller?.shippingAddress?.displayFullCityStateCountry;
    const infoClassName = isMobile ? dibsCss.sassyFontBody : dibsCss.sassyFontBodySizeSmall;

    return (
        <>
            {showLocation && sellerLocation && (
                <div className={infoClassName}>
                    <FormattedMessage
                        id="PdpSharedSellerInfo.location"
                        defaultMessage="Located in {sellerLocation}"
                        values={{ sellerLocation }}
                    />
                </div>
            )}
            {establishmentYear && (
                <div className={infoClassName} data-tn="dealer-establishmentYear">
                    <FormattedMessage
                        id="PdpSharedSellerInfo.establishmentYear"
                        defaultMessage="Established in {establishmentYear}"
                        values={{ establishmentYear }}
                    />
                </div>
            )}
            {showDealerSince && (
                <div className={infoClassName} data-tn="dealer-since">
                    <FormattedMessage
                        id="PdpSharedSellerInfo.since"
                        defaultMessage="1stDibs seller since {dealerSince}"
                        values={{ dealerSince }}
                    />
                </div>
            )}
            {sellerOrderQuantity && sellerOrderQuantity >= MINIMUM_ORDER_QUANTITY && (
                <div className={infoClassName} data-tn="dealer-orderQuantity">
                    <FormattedMessage
                        id="PdpSharedSellerInfo.sellerOrderQuantity"
                        defaultMessage="{sellerOrderQuantity} sales on 1stDibs"
                        values={{ sellerOrderQuantity: formatNumber(sellerOrderQuantity) }}
                    />
                </div>
            )}
            {responseTime && (
                <div className={infoClassName} data-tn="dealer-response-time">
                    <FormattedMessage
                        id="PdpSharedSellerInfo.response_time"
                        defaultMessage="Typical response time: {responseTime}"
                        values={{ responseTime }}
                    />
                </div>
            )}
            {showAssociations && <PdpSharedSellerAssociations seller={seller} />}
        </>
    );
};
